import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterHermanus() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-hermanus",
    name: "Painters Hermanus",
    location: "Hermanus, Western Cape",
    rating: 4.9,
    reviews: 187,
    specialties: ["Coastal Properties", "Whale Watching Season", "Holiday Homes", "Marine-Grade Finishes"],
    image: "https://public.readdy.ai/ai/img_res/36d54350b469eff14cfaf62a4efea75f.jpg",
    verified: true,
    yearsExperience: 15,
    website: "https://painterhermanus.co.za",
    email: "info@painterhermanus.co.za",
    description: "Expert coastal painting services in Hermanus and surrounding areas. We specialize in marine-grade paints and techniques specifically designed for coastal properties exposed to salt air and ocean conditions.",
    services: [
      "Coastal Property Painting",
      "Holiday Home Painting", 
      "Marine-Grade Coatings",
      "Salt Air Protection",
      "Exterior Restoration",
      "Interior Design Painting",
      "Waterproofing Services",
      "Heritage Property Restoration"
    ],
    serviceAreas: ["Hermanus", "Gansbaai", "Stanford", "Kleinmond", "Betty's Bay", "Pringle Bay", "Overberg Coast", "Walker Bay"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20interior%20living%20room%20painted%20by%20professional%20painters%20in%20Hermanus%2C%20coastal%20holiday%20home%20with%20fresh%20walls%2C%20quality%20paint%20finish%2C%20ocean%20view%20through%20windows%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=hermanus-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20house%20painting%20project%20in%20Hermanus%20Western%20Cape%2C%20coastal%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20whale%20watching%20town%20setting%2C%20well-maintained%20property&width=400&height=300&seq=hermanus-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Holiday%20home%20painting%20project%20in%20Hermanus%2C%20professional%20painters%20working%20on%20vacation%20property%20exterior%2C%20quality%20residential%20paint%20finish%2C%20coastal%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=hermanus-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Heritage%20property%20restoration%20painting%20in%20Hermanus%2C%20professional%20painters%20working%20on%20historic%20coastal%20building%2C%20quality%20restoration%20work%2C%20traditional%20architecture%2C%20expert%20craftsmanship&width=400&height=300&seq=hermanus-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Catherine Williams",
        rating: 5,
        comment: "Outstanding work on our Hermanus holiday home. The marine-grade finish has withstood three whale seasons perfectly, and the house still looks as good as new."
      },
      {
        name: "Robert van Zyl", 
        rating: 5,
        comment: "Professional service and exceptional quality. They understand coastal painting like no other contractor we've worked with. Highly recommended."
      },
      {
        name: "Margaret Thompson",
        rating: 5,
        comment: "Excellent painters for heritage properties. Their restoration work on our Victorian cottage was absolutely perfect."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Hermanus | Professional Coastal Painting Services Overberg | House Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Hermanus offers professional painting services in Hermanus, Overberg. Specializing in coastal properties, holiday homes, and marine-grade finishes. Quality coastal painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Hermanus, house painters Hermanus, coastal painting contractors Overberg, holiday home painting, marine-grade finishes');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20stunning%20coastal%20home%20in%20Hermanus%20South%20Africa%2C%20dramatic%20ocean%20views%20with%20whale%20watching%20coastline%2C%20painters%20in%20clean%20uniforms%20with%20marine-grade%20equipment%2C%20pristine%20coastal%20setting%20with%20dramatic%20cliffs%20and%20blue%20ocean%2C%20high%20quality%20coastal%20painting%20project%2C%20organized%20work%20environment&width=1200&height=600&seq=hermanus-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Hermanus - Premier Coastal Painting Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description} With over 15 years of specialized experience in the unique coastal environment of Hermanus and the Overberg region, we have developed unparalleled expertise in protecting and beautifying properties exposed to some of South Africa's most challenging marine conditions.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Hermanus Coastal Painting Expertise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Hermanus, renowned as one of the world's premier whale watching destinations, presents unique challenges for painting contractors. The town's position along Walker Bay exposes properties to constant salt spray, strong coastal winds, and intense UV radiation that can quickly deteriorate standard paint finishes. Our team has spent over a decade perfecting techniques and material selections specifically designed to thrive in these demanding conditions, ensuring that your property maintains its beauty and protection year after year.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Marine-Grade Paint Systems and Protection</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We exclusively use marine-grade paint systems that have been proven in the harshest coastal environments. These specialized coatings provide superior resistance to salt corrosion, moisture penetration, and UV degradation while maintaining excellent color retention and surface integrity. Our paint selection includes advanced polymer technologies that create flexible, breathable barriers that move with your building's natural expansion and contraction cycles, preventing cracking and peeling that commonly affects coastal properties.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Holiday Home and Rental Property Specialists</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Many properties in Hermanus serve as holiday homes or rental accommodations, particularly during the popular whale watching season from June to November. We understand the unique requirements of these properties, including the need for durable finishes that can withstand periods of vacancy, varying levels of maintenance, and high occupancy during peak seasons. Our painting solutions are designed to maintain their appearance and performance with minimal ongoing maintenance, making them ideal for investment properties and holiday homes.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Heritage Property Restoration Excellence</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Hermanus is home to numerous heritage properties, including Victorian cottages and historic fishing buildings that require specialized restoration techniques. Our team has extensive experience working with heritage consultants and conservation specialists to ensure that restoration projects meet both aesthetic and conservation requirements. We use period-appropriate materials and techniques when required, while incorporating modern protective technologies that enhance longevity without compromising historical authenticity.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Seasonal Planning and Whale Season Considerations</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Understanding Hermanus's seasonal patterns is crucial for successful painting projects. We carefully plan our work schedules around the whale watching season, weather patterns, and property usage cycles to minimize disruption while ensuring optimal application conditions. Our team is experienced in working efficiently during favorable weather windows and can adjust project timelines to accommodate the unique rhythms of coastal living and tourism seasons.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Advanced Surface Preparation Techniques</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Proper surface preparation is absolutely critical for coastal painting success. Our comprehensive preparation process includes specialized cleaning techniques to remove salt deposits, thorough sanding and priming procedures, and treatment of any areas affected by salt damage or moisture infiltration. We pay particular attention to areas most vulnerable to coastal conditions, such as window frames, door surrounds, and any metal fixtures that may be prone to corrosion.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Environmental Responsibility and Sustainability</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  As stewards of one of South Africa's most pristine coastal environments, we are committed to environmentally responsible painting practices. We use eco-friendly paint products whenever possible, implement proper waste disposal procedures that protect the marine environment, and follow strict protocols to prevent any contamination of the sensitive coastal ecosystem. Our commitment to sustainability extends to our material selection, application techniques, and cleanup procedures.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20coastal%20home%20in%20Hermanus%20South%20Africa%2C%20whale%20watching%20town%20property%20with%20ocean%20views%2C%20painters%20in%20uniform%20with%20quality%20equipment%2C%20traditional%20coastal%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=hermanus-sidebar-image&orientation=landscape"
                      alt="Professional painters Hermanus coastal house painting services South Africa whale watching town"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Specialized Coastal Painting Services in Hermanus</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Marine-Grade Coating Solutions</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our marine-grade coating services are specifically designed for the extreme coastal conditions found in Hermanus and the surrounding Overberg coast. These advanced coating systems provide unparalleled protection against salt corrosion, moisture damage, and UV degradation while maintaining superior aesthetic appeal throughout the challenging coastal environment.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with marine-grade materials and expert coastal application techniques.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Holiday Home Painting Excellence</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our holiday home painting services in Hermanus are designed to create beautiful, welcoming spaces that enhance your coastal getaway experience. We understand that holiday homes require finishes that can withstand periods of vacancy while maintaining their appeal for guests and family. Our interior painting solutions use premium, low-VOC paints that provide excellent coverage and durability while creating healthy, comfortable living environments.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We work efficiently to minimize disruption to your holiday schedule and can coordinate projects around booking calendars and seasonal usage patterns. Our team is experienced in working with property managers and can provide detailed progress reports and completion documentation for remote property owners.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Exterior Restoration and Protection</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Exterior painting in Hermanus requires specialized knowledge of coastal conditions and advanced protective coating systems. Our comprehensive exterior painting process includes thorough surface preparation, specialized primer application, and multiple coats of marine-grade paint designed to withstand salt spray, strong winds, and intense UV exposure. We use advanced coating technologies that provide superior adhesion and flexibility.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our exterior restoration services also include specialized treatments for areas most vulnerable to coastal damage, such as window frames, door surrounds, and decorative elements. We offer comprehensive maintenance programs that include regular inspections and touch-up services to ensure your coastal property maintains its protection and beauty year-round.
                  </p>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Heritage Property Conservation</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Hermanus is home to numerous heritage properties that require specialized conservation techniques and materials. Our heritage restoration services combine traditional craftsmanship with modern protective technologies to preserve historical authenticity while providing enhanced durability and protection. We work closely with heritage consultants and use period-appropriate materials when required, ensuring that restoration projects meet both aesthetic and conservation standards.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Painting Projects in Hermanus</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout Hermanus and the Overberg coast. Each project demonstrates our commitment to quality workmanship and our expertise in coastal painting applications that withstand the unique challenges of marine environments.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Hermanus Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional coastal painting with marine-grade materials and expert application techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Hermanus and the Overberg coast have to say about our specialized coastal painting services. Our commitment to excellence and customer satisfaction is reflected in these genuine reviews from property owners who have experienced the superior protection and beauty of our marine-grade painting solutions.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Hermanus</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to protect and beautify your Hermanus coastal property with professional marine-grade painting services? Contact us today for a free consultation and quote. We're here to help you achieve exceptional results that withstand the unique challenges of coastal living while enhancing your property's beauty and value.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding Overberg coastal areas</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Coastal Painting Quote</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation quote for your Hermanus coastal painting project. Our experienced team will assess your property and provide detailed recommendations tailored to marine conditions and coastal protection requirements.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
